import { resolveSubscriptionPlanId } from 'src/utils/plan/plan-utils';

import { IWorkspacePlan } from 'src/constants/plan-service';

/** Brand > Growth > Individual > Trial */
const PLAN_SORT_PRIORITY: Record<string, number> = {
  [IWorkspacePlan.planId.plan_brand]: 0,
  [IWorkspacePlan.planId.plan_enterprise]: 0,
  [IWorkspacePlan.planId.plan_growth]: 1,
  [IWorkspacePlan.planId.plan_individual]: 2,
  [IWorkspacePlan.planId.plan_trial]: 3,
  [IWorkspacePlan.planId.plan_demo]: 4,
};

/**
 * Firestore Timestamp·문자열 등을 밀리초로 변환합니다.
 * @param {unknown} timestamp
 * @returns {number | null}
 */
function getWorkspaceTimestampMillis(timestamp: any): number | null {
  if (!timestamp) return null;

  if (typeof timestamp === 'object') {
    if (typeof timestamp.toMillis === 'function') {
      return timestamp.toMillis();
    }
    if (typeof timestamp.seconds === 'number') {
      return timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1_000_000;
    }
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().getTime();
    }
  }

  if (typeof timestamp === 'number') {
    return timestamp < 1e12 ? timestamp * 1000 : timestamp;
  }

  if (typeof timestamp === 'string') {
    const parsed = new Date(timestamp).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
}

/**
 * 워크스페이스 목록 정렬용 플랜 우선순위 (낮을수록 상단)
 * @param {Object | null | undefined} workspace
 * @returns {number}
 */
function getWorkspacePlanSortPriority(workspace: any): number {
  const planId = resolveSubscriptionPlanId(workspace?.subscriptionPlan);

  if (planId && PLAN_SORT_PRIORITY[planId] !== undefined) {
    return PLAN_SORT_PRIORITY[planId];
  }

  const legacyFields = [
    workspace?.subscriptionPlan?.planId,
    workspace?.subscriptionPlan?.planName,
    workspace?.subscriptionPlan?.planType,
  ]
    .filter(Boolean)
    .map((value: unknown) => String(value).toLowerCase());

  for (const field of legacyFields) {
    if (field.includes('brand')) return PLAN_SORT_PRIORITY.plan_brand;
    if (field.includes('growth')) return PLAN_SORT_PRIORITY.plan_growth;
    if (field.includes('individual')) return PLAN_SORT_PRIORITY.plan_individual;
    if (field.includes('trial')) return PLAN_SORT_PRIORITY.plan_trial;
  }

  return Number.MAX_SAFE_INTEGER;
}

function compareMillisDesc(millisA: number | null, millisB: number | null): number {
  if (millisA === null && millisB === null) return 0;
  if (millisA === null) return 1;
  if (millisB === null) return -1;
  return millisB - millisA;
}

/**
 * 활성 워크스페이스를 플랜 순으로, 만료 워크스페이스는 만료일 내림차순으로 정렬합니다.
 * @param {Array<Object>} workspaces
 * @returns {Array<Object>}
 */
export function sortWorkspacesForList(workspaces: any[] = []): any[] {
  if (!Array.isArray(workspaces) || workspaces.length === 0) {
    return [];
  }

  const now = Date.now();

  const metas = workspaces.map((workspace: any) => {
    const millis = getWorkspaceTimestampMillis(
      workspace?.subscriptionPlan?.planEndTime,
    );
    const isExpired = millis === null || millis < now;
    return { workspace, millis, isExpired };
  });

  const activeMetas = metas.filter((meta) => !meta.isExpired);
  const expiredMetas = metas.filter((meta) => meta.isExpired);

  activeMetas.sort((a, b) => {
    const priorityA = getWorkspacePlanSortPriority(a.workspace);
    const priorityB = getWorkspacePlanSortPriority(b.workspace);
    if (priorityA !== priorityB) return priorityA - priorityB;
    return compareMillisDesc(a.millis, b.millis);
  });

  expiredMetas.sort((a, b) => compareMillisDesc(a.millis, b.millis));

  return [...activeMetas, ...expiredMetas].map((meta) => meta.workspace);
}

/**
 * 워크스페이스 목록 플랜 Label용 문구 — 플랜 이름은 대문자(「LITE」 · 「BRAND 만료」)
 * @param {Object | null | undefined} subscriptionPlan
 * @returns {string}
 */
export function formatWorkspacePlanLabel(subscriptionPlan: any): string {
  if (!subscriptionPlan) return '';
  const planName = String(subscriptionPlan.planName ?? '').trim().toUpperCase();
  const expiredSuffix = subscriptionPlan.planStatus === 'expired' ? ' 만료' : '';
  return `${planName}${expiredSuffix}`.trim();
}
